import { useState } from 'react'
import { KeyboardArrowDown } from '@mui/icons-material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useNavigate } from 'react-router-dom'
import { CONSTANTS } from '../../../Constants/Constant'
import { MUIGrid } from '../../Shared'
import AssetStateLabel from '../../Labels/AssetStateLabel'
import { getDateInFormat } from '../../../Helpers/Util'
import style from '../../Molecule/Molecule.module.scss'
import headerStyles from './HeaderComponent.module.scss'
import { TooltipText } from '../../Shared/TooltipText/TooltipText'
import HeaderContent from './HeaderContent'
import { HeaderComponentModal } from '../../../Models/HeaderComponent'

const AccordionIcon = ({ isOpen }: { isOpen: boolean }) => (
  <div className={headerStyles.accordionIcon}>
    {isOpen ? <KeyboardArrowUpIcon className='col33' /> : <KeyboardArrowDown className='col33' />}
  </div>
)

const ExpiringOnSection = ({
  unfeaturedTime,
  dateFormat,
}: {
  unfeaturedTime: any
  dateFormat: string
}) => (
  <div className='schedule_header'>
    <div className='schedule_header_text'>
      <span>Expiring On :</span>
      <span>{unfeaturedTime ? getDateInFormat(unfeaturedTime, dateFormat) : 'NA'}</span>
    </div>
    <div className='schedule_header_icon' />
  </div>
)

const formatHeaderValue = (value: any, type: string | undefined, dateFormat: string): string => {
  if (typeof value === 'string' && type === 'date') {
    return getDateInFormat(value, dateFormat)
  }
  return String(value || 'NA')
}

export default function HeaderComponent({
  headerLabel,
  status = 'NA',
  type = 'NA',
  layout = 'NA',
  title = 'NA',
  onDeleteIconClick,
  headerData,
  headerEndTimeVisible = false,
  unfeaturedTime = 'NA',
  isDeleted,
  dynamicHeaderFirstRow,
}: HeaderComponentModal) {
  const [showDetails, setShowDetails] = useState(false)
  const navigate = useNavigate()
  const { DATE_N_TIME_FORMAT_SECONDS } = CONSTANTS

  const firstRowData = dynamicHeaderFirstRow

  const accordionClassName = `d-flex justify-content-between align-items-center ${
    headerStyles.widgetHeaderRow
  } ${style.asset_header_static} ${showDetails ? headerStyles.AccordionOpen : ''}`

  const dynamicContentMuiGrid = (data: any[]) =>
    data.map((header: any) => (
      <MUIGrid key={header.title} xs={6} lg={4}>
        <p className={`${headerStyles.headerDataWrapper}`}>
          <span style={{ fontWeight: '400', marginRight: '0.25rem' }} className={'col34'}>
            {`${header.title} :`}
          </span>
          <span className={`col36 ${style.header_value}`}>
            {formatHeaderValue(header.value, header.type, DATE_N_TIME_FORMAT_SECONDS)}
          </span>
        </p>
      </MUIGrid>
    ))

  const renderDynamicFirstRow = (rowData: any[]) => (
    <div className={accordionClassName}>
      <MUIGrid container columnSpacing={2} rowSpacing={2} style={{ margin: 'auto' }}>
        {dynamicContentMuiGrid(rowData)}
        <AccordionIcon isOpen={showDetails} />
      </MUIGrid>
    </div>
  )

  const renderDefaultFirstRow = () => (
    <MUIGrid container className={accordionClassName}>
      <MUIGrid className={headerStyles.widgetHeaderCol}>
        <p className='d-flex align-items-center' style={{ gap: '10px' }}>
          <span className={`col34 ${headerStyles.widgetHeaderTitle}`}>
            {`${headerLabel} Type`} :
          </span>
          <span className={`col36 capitalize ${style.f_12_w_600}`}>{type}</span>
          <AssetStateLabel state={isDeleted ? 'Archived' : status} />
        </p>
      </MUIGrid>
      <MUIGrid
        className={`d-flex align-items-center ${headerStyles.widgetHeaderCol}`}
        style={{ maxWidth: '50%', wordBreak: 'break-all', gap: '10px' }}
      >
        <span className={`col34 ${headerStyles.widgetHeaderTitle}`}>Layout :</span>
        <span className={`col36 ${style.header_value}`}>{layout || 'NA'}</span>
      </MUIGrid>
      <MUIGrid
        className={`d-flex align-items-center ${headerStyles.widgetHeaderCol} ${headerStyles.widgetTitle}`}
      >
        <span className={`col34 ${headerStyles.widgetHeaderTitle}`}>Title :</span>
        <span className={`col36 ${style.w_600}`}>
          <span className='d-flex align-items-center'>
            {title ? <TooltipText text={title} length={60} /> : 'NA'}
          </span>
        </span>
      </MUIGrid>
      {headerEndTimeVisible && (
        <ExpiringOnSection
          unfeaturedTime={unfeaturedTime}
          dateFormat={DATE_N_TIME_FORMAT_SECONDS}
        />
      )}
      <AccordionIcon isOpen={showDetails} />
    </MUIGrid>
  )

  return (
    <div className='asset__editor-header'>
      <div className='asset__editor-info'>
        <div style={{ position: 'relative' }}>
          <div
            aria-hidden={true}
            className='cursor-pointer mt-1'
            data-testid='asset_header_accordion'
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {firstRowData && firstRowData.length > 0
              ? renderDynamicFirstRow(firstRowData)
              : renderDefaultFirstRow()}
          </div>
          {showDetails && (
            <HeaderContent
              headerData={headerData}
              navigate={navigate}
              onDeleteIconClick={onDeleteIconClick}
              isDeleted={isDeleted}
              status={status}
              layout={layout}
            />
          )}
        </div>
      </div>
    </div>
  )
}
